import ExcelJS from 'exceljs';
import sapMasterList from '../data/sapMasterList.json';

// Helper to find the price in the master list
const findEquipmentData = (searchKey) => {
  if (!searchKey) return null;
  const lowerKey = searchKey.toLowerCase();
  
  // Try exact match first
  let match = sapMasterList.find(item => item['£ KEY']?.toLowerCase() === lowerKey);
  
  // If not found, try partial match
  if (!match) {
    match = sapMasterList.find(item => item['£ KEY']?.toLowerCase().includes(lowerKey));
  }
  
  return match || null;
};

export const calculateTotalCost = (selections, numPipelines) => {
  let totalCost = 0;
  
  if (!selections) return totalCost;

  for (let pipeIdx = 0; pipeIdx < numPipelines; pipeIdx++) {
    const categories = selections[pipeIdx] || {};
    for (const [catId, groups] of Object.entries(categories)) {
      for (const [groupName, itemsSet] of Object.entries(groups)) {
        if (!itemsSet || itemsSet.size === 0) continue;
        
        const items = Array.isArray(itemsSet) ? itemsSet : Array.from(itemsSet);
        
        for (const item of items) {
          const isGeneric = ['CRCE Supply', 'Client Supply', 'Generic'].includes(item);
          const searchKey = isGeneric ? groupName : item;
          
          const eqData = findEquipmentData(searchKey);
          if (eqData && eqData['__EMPTY_1']) {
            const price = parseFloat(eqData['__EMPTY_1']) || 0;
            totalCost += price;
          }
        }
      }
    }
  }
  
  return totalCost;
};

// Helper for cell styles
const setHeaderStyle = (cell, isRed = false) => {
  cell.font = { bold: true, size: 9 };
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
  cell.border = {
    top: { style: 'thin' }, left: { style: 'thin' },
    bottom: { style: 'thin' }, right: { style: 'thin' }
  };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: isRed ? 'FFFF0000' : 'FF9FC5E8' } // Light blue or Red
  };
};

export const generateMobilistExcel = async (mobilist) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(mobilist.projectType || 'CCAB Offshore');

  // --- Column Widths ---
  sheet.getColumn(1).width = 12; // A: WBS #
  sheet.getColumn(2).width = 8;  // B: Ret/Con
  sheet.getColumn(3).width = 8;  // C: Type
  sheet.getColumn(4).width = 8;  // D: Photo
  sheet.getColumn(5).width = 8;  // E: CofO
  sheet.getColumn(6).width = 8;  // F: Unit Kg
  sheet.getColumn(7).width = 15; // G: Owner
  sheet.getColumn(8).width = 5;  // H: #
  sheet.getColumn(9).width = 45; // I: Description / Category
  sheet.getColumn(10).width = 15; // J: HS Code
  sheet.getColumn(11).width = 20; // K: SAP #
  sheet.getColumn(12).width = 10; // L: Quantity
  sheet.getColumn(13).width = 12; // M: Unit Cost
  sheet.getColumn(14).width = 12; // N: Total Cost
  sheet.getColumn(15).width = 30; // O: Notes

  // --- Top Information Block ---
  sheet.getCell('I3').value = 'CCAB - Closed Cycle Abrasive Blasting (Offshore)';
  sheet.getCell('I3').font = { bold: true, underline: true, size: 10 };

  // Project Info Left (A-B)
  sheet.getCell('A4').value = 'Enter Project # Here';
  sheet.getCell('A4').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A4').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B4').value = mobilist.projectNumber || '';
  
  sheet.getCell('A5').value = 'For Pull WBS Element';
  sheet.getCell('A5').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF93C47D' } }; // Light Green
  sheet.getCell('A5').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B5').value = mobilist.wbsElement || '';

  // Description / Type Legend (A-B)
  sheet.getCell('A7').value = 'Description Column';
  sheet.getCell('A7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A7').font = { bold: true, size: 9 };
  sheet.getCell('A7').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };

  sheet.getCell('A8').value = 'Type';
  sheet.getCell('A8').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A8').font = { bold: true, size: 9 };
  sheet.getCell('A8').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B8').value = 'TBC';
  sheet.getCell('B8').font = { color: { argb: 'FFFF0000' }, size: 9 };

  sheet.getCell('A9').value = 'FIC SOW (Qty & Size)';
  sheet.getCell('A9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A9').font = { bold: true, size: 9 };
  sheet.getCell('A9').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B9').value = 'TBC';
  sheet.getCell('B9').font = { color: { argb: 'FFFF0000' }, size: 9 };

  // Pipe Specs (I-K)
  const pipeSpecs = [
    { row: 5, label: 'PIPE STEEL OD' },
    { row: 6, label: 'PARENT COATING' },
    { row: 7, label: 'CWC CUTBACKS' },
    { row: 8, label: 'STEEL CUTBACKS' },
    { row: 9, label: 'PIPE WALL THICKNESS' }
  ];
  pipeSpecs.forEach(spec => {
    sheet.getCell(`I${spec.row}`).value = spec.label;
    sheet.getCell(`I${spec.row}`).font = { bold: true, size: 9 };
    sheet.getCell(`K${spec.row}`).value = 'TBC';
    sheet.getCell(`K${spec.row}`).font = { color: { argb: 'FFFF0000' }, size: 9, bold: true };
  });

  // Project Specs (I-K)
  const specsStartRow = 11;
  sheet.getCell(`I${specsStartRow}`).value = 'PIPELAY MODE';
  sheet.getCell(`I${specsStartRow}`).font = { bold: true, size: 9 };
  sheet.getCell(`K${specsStartRow}`).value = mobilist.pipelayMode?.toUpperCase() || mobilist.projectType?.toUpperCase() || 'OFFSHORE';
  sheet.getCell(`K${specsStartRow}`).font = { bold: true, size: 9 };

  sheet.getCell(`I${specsStartRow+2}`).value = 'PRODUCTION DAYS';
  sheet.getCell(`I${specsStartRow+2}`).font = { bold: true, size: 9 };
  sheet.getCell(`K${specsStartRow+2}`).value = mobilist.productionDays || '';
  sheet.getCell(`K${specsStartRow+2}`).font = { bold: true, size: 9 };

  sheet.getCell(`I${specsStartRow+4}`).value = 'NUMBER OF PERSONNEL';
  sheet.getCell(`I${specsStartRow+4}`).font = { bold: true, size: 9 };
  sheet.getCell(`K${specsStartRow+4}`).value = mobilist.numberOfPersonnel || mobilist.totalPersonnel || '';
  sheet.getCell(`K${specsStartRow+4}`).font = { bold: true, size: 9 };

  sheet.getCell(`I${specsStartRow+6}`).value = 'NUMBER OF PROJECTED FIELD JOINTS';
  sheet.getCell(`I${specsStartRow+6}`).font = { bold: true, size: 9 };
  const totalJoints = (mobilist.pipelines || []).reduce((acc, curr) => acc + (parseInt(curr.numberOfFieldJoints) || 0), 0);
  sheet.getCell(`K${specsStartRow+6}`).value = totalJoints || '';
  sheet.getCell(`K${specsStartRow+6}`).font = { bold: true, size: 9 };

  // Project Info Right (M-N)
  sheet.getCell('M4').value = 'REVISION: 0';
  sheet.getCell('M4').font = { bold: true, size: 9 };
  sheet.getCell('M4').alignment = { horizontal: 'right' };
  
  sheet.getCell('M6').value = 'DATE ISSUED';
  sheet.getCell('M6').font = { bold: true, size: 9 };
  sheet.getCell('M6').alignment = { horizontal: 'right' };
  sheet.getCell('N6').value = 'TBC';
  sheet.getCell('N6').font = { bold: true, color: { argb: 'FFFF0000' }, size: 9 };
  
  sheet.getCell('M7').value = 'BY';
  sheet.getCell('M7').font = { bold: true, size: 9 };
  sheet.getCell('M7').alignment = { horizontal: 'right' };
  sheet.getCell('N7').value = mobilist.createdBy || '';
  sheet.getCell('N7').font = { bold: true, size: 9 };

  // Status Legend Right (N)
  const legRow = 10;
  sheet.getCell(`N${legRow}`).value = 'P Column';
  sheet.getCell(`N${legRow}`).font = { bold: true, size: 9 };
  sheet.getCell(`N${legRow}`).alignment = { horizontal: 'center' };
  
  const statuses = [
    { name: 'No Action', color: 'FFFFFFFF' }, // White
    { name: 'On Order', color: 'FFFF9900' }, // Orange
    { name: 'Picked or Delivered', color: 'FFFFFF00' }, // Yellow
    { name: 'Picked & Packed', color: 'FF00FF00' }, // Green
    { name: 'Sourced Locally', color: 'FFCCFFCC' }, // Light Green
    { name: 'On Hold', color: 'FFFF0000' } // Red
  ];
  statuses.forEach((status, idx) => {
    const cell = sheet.getCell(`N${legRow + 1 + idx}`);
    cell.value = status.name;
    cell.font = { size: 9, bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: status.color } };
    cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  });

  // Banners Row
  let currentRow = 20;
  
  // Left Banner
  sheet.mergeCells(`A${currentRow}:H${currentRow}`);
  const leftBanner = sheet.getCell(`A${currentRow}`);
  leftBanner.value = 'Supply Chain / Mobilisation (Only)';
  leftBanner.font = { bold: true, size: 9 };
  leftBanner.alignment = { horizontal: 'center', vertical: 'middle' };
  leftBanner.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF93C47D' } }; // Light Green
  leftBanner.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'medium' }, right: { style: 'medium' } };

  // Right Banner
  sheet.mergeCells(`I${currentRow}:O${currentRow}`);
  const rightBanner = sheet.getCell(`I${currentRow}`);
  rightBanner.value = 'FIRING LINE';
  rightBanner.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
  rightBanner.alignment = { horizontal: 'center', vertical: 'middle' };
  rightBanner.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF0000' } }; // Red
  rightBanner.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'medium' }, right: { style: 'medium' } };

  currentRow += 1;

  // --- Group Items by Category ---
  const categorizedItems = {};
  
  const numPipes = parseInt(mobilist.numberOfPipelines, 10) || 1;
  const selections = mobilist.selections || {};

  for (let pipeIdx = 0; pipeIdx < numPipes; pipeIdx++) {
    const categories = selections[pipeIdx] || {};
    for (const [catId, groups] of Object.entries(categories)) {
      for (const [groupName, itemsSet] of Object.entries(groups)) {
        if (!itemsSet) continue;
        
        const items = Array.isArray(itemsSet) ? itemsSet : Array.from(itemsSet);
        for (const item of items) {
          const isGeneric = ['CRCE Supply', 'Client Supply', 'Generic'].includes(item);
          const searchKey = isGeneric ? groupName : item;
          const eqData = findEquipmentData(searchKey);
          
          let extCat = 'UNCATEGORIZED';
          if (eqData && eqData['__EMPTY_10']) {
            extCat = eqData['__EMPTY_10'].toUpperCase();
          } else if (eqData && eqData['__EMPTY_9']) {
            extCat = eqData['__EMPTY_9'].toUpperCase();
          }

          if (!categorizedItems[extCat]) categorizedItems[extCat] = [];
          
          categorizedItems[extCat].push({
            groupName,
            item,
            eqData,
            searchKey,
            isGeneric
          });
        }
      }
    }
  }

  // --- Render Categories ---
  const columns = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O'];

  for (const [categoryName, items] of Object.entries(categorizedItems)) {
    // Spacer row
    currentRow++;

    // Sub-Header Row
    const headerRow = sheet.getRow(currentRow);
    headerRow.values = [
      'WBS #', 'Ret/Con', 'Type', 'Photo', 'CofO', 'Unit Kg', 'Owner', '#', 
      categoryName, 'HS Code', 'SAP #', 'Quantity', 'Unit Cost £', 'Total Cost £', 'Notes'
    ];
    
    columns.forEach(col => setHeaderStyle(sheet.getCell(`${col}${currentRow}`)));
    currentRow++;

    let blockStartRow = currentRow;

    // Data Rows
    items.forEach(({ eqData, searchKey, isGeneric, item }) => {
      const price = eqData ? parseFloat(eqData['__EMPTY_1']) || 0 : 0;
      const qty = 1;
      const total = price * qty;
      const owner = eqData ? eqData['__EMPTY_8'] : '';
      
      const row = sheet.getRow(currentRow);
      row.values = [
        mobilist.wbsElement || '',               // A: WBS
        eqData ? eqData['__EMPTY_4'] : '',       // B: Ret/Con
        eqData ? eqData['__EMPTY_5'] : '',       // C: Type
        '',                                      // D: Photo
        '',                                      // E: CofO
        '',                                      // F: Unit Kg
        owner,                                   // G: Owner
        '',                                      // H: #
        eqData ? eqData['£ KEY'] : searchKey,    // I: Description
        eqData ? eqData['__EMPTY_2'] : '',       // J: HS Code
        eqData ? eqData['__EMPTY'] : '',         // K: SAP #
        qty,                                     // L: Quantity
        price,                                   // M: Unit Cost
        total,                                   // N: Total Cost
        item === 'Client Supply' ? 'CRCE or Client Supply?' : '' // O: Notes
      ];

      // Formatting
      row.getCell(13).numFmt = '0.00'; // Unit Cost
      row.getCell(14).numFmt = '0.00'; // Total Cost

      // Styling based on data
      if (owner && owner.toLowerCase().includes('workshop')) {
        row.getCell(7).font = { color: { argb: 'FFFF0000' }, size: 9 };
      } else {
        row.getCell(7).font = { size: 9 };
      }

      if (item === 'Client Supply') {
        row.getCell(15).font = { color: { argb: 'FFFF0000' }, size: 9 };
      } else {
        row.getCell(15).font = { size: 9 };
      }

      // Base font and borders
      columns.forEach(col => {
        const cell = sheet.getCell(`${col}${currentRow}`);
        cell.font = cell.font || { size: 9 };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        
        if (['A', 'B', 'C', 'G', 'L', 'M', 'N'].includes(col)) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { vertical: 'middle' };
        }
      });

      currentRow++;
    });

    let blockEndRow = currentRow - 1;

    // Sub Category Total Row
    sheet.mergeCells(`I${currentRow}:M${currentRow}`);
    const subTotalLabel = sheet.getCell(`I${currentRow}`);
    subTotalLabel.value = 'Sub Category Total ->';
    subTotalLabel.font = { bold: true, color: { argb: 'FFFF0000' }, size: 9 };
    subTotalLabel.alignment = { horizontal: 'right' };
    subTotalLabel.border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };
    
    // Fill the empty cells before SubTotal with bottom red border
    for(let i = 1; i <= 8; i++) {
      sheet.getCell(currentRow, i).border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };
    }

    const subTotalValue = sheet.getCell(`N${currentRow}`);
    subTotalValue.value = { formula: `SUM(N${blockStartRow}:N${blockEndRow})` };
    subTotalValue.numFmt = '0.00';
    subTotalValue.font = { bold: true, color: { argb: 'FFFF0000' }, size: 9 };
    subTotalValue.alignment = { horizontal: 'center' };
    subTotalValue.border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };

    const notesTotal = sheet.getCell(`O${currentRow}`);
    notesTotal.border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };

    currentRow++;
  }

  // --- Generate and Download ---
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `Mobilist_${mobilist.projectName || 'Export'}.xlsx`;
  a.click();
  
  URL.revokeObjectURL(url);
};
